import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Bar, BarChart, CartesianGrid, XAxis } from "recharts"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"
import type { OutstandingAgeBucketKey, OutstandingAgeingResponse } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * Outstanding by age — 0-30 / 31-60 / 61-90 / 90+ days (§17.4, §17.5).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE MOST ACTIONABLE THING IN THE APP, WHICH IS WHY IT IS A CHART ON THIS
 * SCREEN AND NOT ON HOME. §11.2 bans an analytics page "with charts" because the
 * owner is a workshop operator rather than an analyst — and grants this
 * secondary screen expressly to feed the CA. The bar that earns its place here
 * answers "who do I phone this morning", and the shape of the four buckets
 * answers it faster than four numbers in a row do: one tall bar on the right is
 * a different morning from four short ones on the left.
 *
 * IT IS "OWED", NOT "OVERDUE", AND NOTHING HERE MAY SAY OTHERWISE. This system
 * models no credit terms and stores no due date (§17.5), so calling a 91-day-old
 * bill overdue would be the app inventing an agreement the business never made.
 * The last bucket is labelled `90+`, exactly as the API names it.
 *
 * IT IS NOT SCOPED TO THE PERIOD SELECTOR, and the caption says so. Outstanding
 * is a to-do list, not a period figure: a bill raised in March is precisely the
 * one still worth chasing in August, and scoping this to "this month" would hide
 * the debts that have been waiting longest. Every other figure on the Reports
 * screen IS period-scoped, so leaving this unsaid would be the one caption that
 * quietly means something else.
 *
 * THE FIGURES ARE THE SERVER'S FOLD OF `readOutstandingInvoices` — the SAME
 * producer behind the home screen's unpaid line and the unpaid register — so
 * this chart, that card and the list it opens cannot disagree. Nothing is summed
 * here; even the total in the header is `totals.outstandingPaise` off the wire.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/**
 * The bucket labels, keyed by the API's own bucket key.
 *
 * The i18n keys are spelled `d31to60` rather than `31-60` because a dot-and-dash
 * key is not what `t()` takes here, and because the completeness scan reads
 * literal `t("…")` calls — a key assembled from a variable is invisible to it,
 * so these live in one map that is easy to check against both locale files.
 */
const AGE_LABEL_KEY: Record<OutstandingAgeBucketKey, string> = {
  "0-30": "reports.age.upTo30",
  "31-60": "reports.age.d31to60",
  "61-90": "reports.age.d61to90",
  "90+": "reports.age.over90",
}

/** Recharts needs one flat row per bar. */
interface Column {
  key: OutstandingAgeBucketKey
  label: string
  outstanding: number
  invoiceCount: number
}

export function ChartOutstandingByAge({
  data,
  loading,
}: {
  data: OutstandingAgeingResponse | undefined
  loading: boolean
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const columns = useMemo<Column[]>(() => {
    if (data === undefined) return []

    /*
     * ALL FOUR BUCKETS, INCLUDING THE EMPTY ONES — the API always sends four and
     * this never filters them. A chart drawn from only the non-empty buckets
     * puts "90+" next to "0-30" and reads as though everything owed is ancient.
     *
     * PAISE, the integer the server sent, carried straight into recharts:
     * it only compares these against each other to scale the plot, and dividing
     * by 100 here is the exact moment money becomes a float (CLAUDE.md).
     */
    return data.buckets.map((bucket) => ({
      key: bucket.key,
      label: t(AGE_LABEL_KEY[bucket.key]),
      outstanding: bucket.outstandingPaise,
      invoiceCount: bucket.invoiceCount,
    }))
  }, [data, t])

  const chartConfig = {
    outstanding: { label: t("reports.chart.owed"), color: "var(--primary)" },
  } satisfies ChartConfig

  const openBucket = (column: Column | undefined) => {
    /*
     * THE UNPAID REGISTER, AND THE SAME DESTINATION FOR ALL FOUR BARS — which is
     * honest rather than lazy. `GET /payments/outstanding` takes no parameters
     * at all (its whole point is that the position is complete), so there is no
     * age filter to hand it; a link promising "the 90+ ones" would land him on
     * the full list and look broken. The register arrives OLDEST FIRST, so the
     * bills behind the rightmost bar are already at the top of it.
     */
    if (column === undefined || column.invoiceCount === 0) return
    navigate(routes.invoicesUnpaid)
  }

  // Loaded, and nothing is owed. The card removes itself rather than drawing a
  // flat line at zero: an empty chart is a claim that needs reading, and "every
  // bill is settled" is better said by the absence of a chase list.
  if (!loading && data !== undefined && data.totals.invoiceCount === 0) return null

  return (
    <Card className="@container/card">
      <CardHeader>
        <CardTitle>{t("reports.chart.ageTitle")}</CardTitle>
        <CardDescription>{t("reports.chart.ageSubtitle")}</CardDescription>
        {data === undefined ? null : (
          <CardAction className="text-muted-foreground text-sm tabular-nums">
            {/* The server's own total, never a sum of the bars — §6, and the one
              * place a client-side reduce could disagree with the home screen. */}
            {formatRupees(data.totals.outstandingPaise)}
          </CardAction>
        )}
      </CardHeader>

      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        {loading || data === undefined ? (
          <Skeleton className="h-[220px] w-full" />
        ) : (
          <>
            <ChartContainer config={chartConfig} className="aspect-auto h-[220px] w-full">
              <BarChart
                data={columns}
                margin={{ left: 4, right: 4 }}
                onClick={(state) => {
                  const index = state?.activeTooltipIndex
                  if (typeof index === "number") openBucket(columns[index])
                }}
                className="cursor-pointer"
              >
                <CartesianGrid vertical={false} />

                <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />

                {/*
                  * NO Y AXIS, exactly as `chart-billed-by-month` has none. Rupee
                  * tick labels would either repeat a figure the owner did not
                  * ask for four times up the side, or be abbreviated to "3.4L" —
                  * and abbreviating means a rupee formatter outside
                  * `packages/tax`, which is the second formatter CLAUDE.md
                  * exists to prevent. Every rupee amount here goes through
                  * `formatRupees`: the header total, the tooltip and the buttons
                  * below.
                  */}

                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      indicator="dot"
                      labelFormatter={(_label, payload) => {
                        const column = payload?.[0]?.payload as Column | undefined
                        if (column === undefined) return ""
                        return `${column.label} · ${t("reports.customerBills", {
                          count: column.invoiceCount,
                        })}`
                      }}
                      formatter={(value) => (
                        <span className="font-medium tabular-nums">
                          {formatRupees(Number(value))}
                        </span>
                      )}
                    />
                  }
                />

                <Bar dataKey="outstanding" fill="var(--color-outstanding)" radius={4} />
              </BarChart>
            </ChartContainer>

            {/*
              * THE ACCESSIBLE PRIMARY, NOT A FALLBACK. An SVG rectangle is not
              * reachable by keyboard or screen reader and cannot honestly be
              * made so — and §11.4 has the owner reading this in Marathi on a
              * phone, where a 20px-wide slice of a bar is a coin toss (§11.1
              * wants 48px targets, and he is 55). These four rows carry the
              * figure in words and are the real tap target.
              */}
            <ul
              className="mt-4 grid grid-cols-2 gap-2 @[560px]/card:grid-cols-4"
              aria-label={t("reports.chart.ageTitle")}
            >
              {columns.map((column) => (
                <li key={column.key}>
                  <button
                    type="button"
                    disabled={column.invoiceCount === 0}
                    onClick={() => openBucket(column)}
                    className="hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring flex w-full min-h-11 flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-40"
                  >
                    <span className="text-muted-foreground text-xs">{column.label}</span>
                    <span className="text-sm font-medium tabular-nums">
                      {formatRupees(column.outstanding)}
                    </span>
                    <span className="text-muted-foreground text-xs tabular-nums">
                      {t("reports.customerBills", { count: column.invoiceCount })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  )
}
